import { useState, useEffect, useCallback, useRef } from 'react';
import OverlayPalette from './OverlayPalette';
import LayoutCanvas from './LayoutCanvas';
import PropertiesPanel from './PropertiesPanel';
import SyncProgress from './SyncProgress';
import { detectRenderSync, submitPreviewClip, getPreviewClipStatus, getOverlayStyles } from '../../../services/api';
import { OVERLAY_TYPES, overlaysMissingChannel } from './overlayDefaults';
import { commitHistory, createHistory, normalizePosition, serializeOverlays, travelHistory } from './editorModel';
import useLayerPreview from './useLayerPreview';
import { repairGaugeSettings } from './gaugeSettings';
import { classifySyncResult, parseSyncOffset } from './syncPolicy';
import HelpButton from '../../HelpButton/HelpButton';
import styles from './LayoutEditor.module.css';

const PREVIEW_STATUS_LABELS = {
  queued:         'Queued…',
  extracting_gps: 'Extracting GPS…',
  syncing:        'Syncing…',
  precomputing:   'Pre-computing overlays…',
  encoding:       'Encoding…',
  completed:      'Complete!',
  failed:         'Failed',
  cancelled:      'Cancelled',
  unknown:        'Checking…',
};

export default function LayoutEditor({
  sessionId,
  availableChannels,
  unitPrefs,
  sessionDuration,
  initialOverlays,
  resolution,
  syncOffset,
  isAdmin = false,
  assertSyncForIndex = false,
  setAssertSyncForIndex,
  setSyncOffset,
  videoTransform = 'none',
  setVideoTransform,
  submitting = false,
  submitError = null,
  onBack,
  onDone,
}) {
  const [history, setHistory] = useState(() => createHistory(initialOverlays.map(o => ({
    ...repairGaugeSettings(o, availableChannels, unitPrefs), position: normalizePosition(o.position, o.type),
  }))));
  const historyRef = useRef(history);
  const overlays = history.present;
  const [selectedId, setSelectedId] = useState(null);
  const [overlayStyles, setOverlayStyles] = useState({});
  const [previewTimeSec, setPreviewTimeSec] = useState(0);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncDetecting, setSyncDetecting] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncWarning, setSyncWarning] = useState(null);
  const [syncInfo, setSyncInfo] = useState(null);
  const [syncReady, setSyncReady] = useState(() => parseSyncOffset(syncOffset) != null);

  // Preview clip state
  const [previewClipOpen, setPreviewClipOpen] = useState(false);
  const [previewOffsetPct, setPreviewOffsetPct] = useState(0.25);
  const [previewDurationSec, setPreviewDurationSec] = useState(20);
  const [previewJobId, setPreviewJobId] = useState(null);
  const [previewStatus, setPreviewStatus] = useState(null);
  const [previewSubmitting, setPreviewSubmitting] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const previewPollRef = useRef(null);

  const overlaysRef = useRef(overlays);
  const syncOffsetRef = useRef(syncOffset);
  const syncReadyRef = useRef(parseSyncOffset(syncOffset) != null);
  const syncRequestIdRef = useRef(0);  // ignores stale detection responses after navigation/retry
  const autoDetectStartedRef = useRef(false);
  const mountedRef = useRef(false);
  const nextIdRef = useRef(Date.now());

  const preview = useLayerPreview({
    sessionId, resolution, overlays, time: previewTimeSec,
    syncOffset: parseSyncOffset(syncOffset), ready: syncReady && !syncDetecting, videoTransform,
  });

  const commitOverlays = useCallback((next, group = null) => {
    const updated = commitHistory(historyRef.current, next, group);
    historyRef.current = updated;
    overlaysRef.current = updated.present;
    setHistory(updated);
  }, []);

  const navigateHistory = useCallback(direction => {
    const updated = travelHistory(historyRef.current, direction);
    historyRef.current = updated;
    overlaysRef.current = updated.present;
    setHistory(updated);
    setSelectedId(id => updated.present.some(o => o._id === id) ? id : null);
  }, []);

  // Keep refs in sync with state/props
  useEffect(() => {
    overlaysRef.current = overlays;
  }, [overlays]);

  useEffect(() => {
    syncOffsetRef.current = syncOffset;
  }, [syncOffset]);

  // Fetch the VTR overlay style libraries once, keyed by overlay type
  // (gauge / bar / track_map / graph). Empty object if VTR is unreachable.
  useEffect(() => {
    let alive = true;
    getOverlayStyles().then(s => { if (alive) setOverlayStyles(s && typeof s === 'object' ? s : {}); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Keep the automatic sync request valid through StrictMode's setup/cleanup.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddOverlay = useCallback((type, position) => {
    const id = nextIdRef.current++;
    const extra = {};
    if (type === 'lap_table' || type === 'laptime') {
      const distLap = availableChannels.find(ch => ch.name.toLowerCase() === 'distance lap');
      if (distLap) extra.channel = distLap.name;
      if (type === 'laptime') extra.label = 'LAP';
    }
    commitOverlays([...overlaysRef.current, { _id: id, type, position: normalizePosition(position, type), ...extra }]);
    setSelectedId(id);
  }, [availableChannels, commitOverlays]);

  // Group a short burst of property edits (including multi-field unit changes)
  // into one undo step. Pointer gestures commit only once, at release.
  const handleUpdateOverlay = useCallback((id, field, value) => {
    const updated = overlaysRef.current.map(o => o._id === id ? { ...o, [field]: value } : o);
    commitOverlays(updated, `properties:${id}`);
  }, [commitOverlays]);

  const handleUpdateOverlayMany = useCallback((id, patch) => {
    const updated = overlaysRef.current.map(o => {
      if (o._id !== id) return o;
      const next = repairGaugeSettings({ ...o, ...patch }, availableChannels, unitPrefs);
      return { ...next, position: normalizePosition(next.position, next.type) };
    });
    commitOverlays(updated);
  }, [commitOverlays, availableChannels, unitPrefs]);

  const handlePositionChange = useCallback((id, newPosition) => {
    commitOverlays(overlaysRef.current.map(o => o._id === id
      ? { ...o, position: normalizePosition(newPosition, o.type) } : o));
  }, [commitOverlays]);

  const handleDeleteOverlay = useCallback((id) => {
    const filtered = overlaysRef.current.filter(o => o._id !== id);
    commitOverlays(filtered);
    setSelectedId(prev => (prev === id ? null : prev));
  }, [commitOverlays]);

  const handleDuplicateOverlay = useCallback(id => {
    const source = overlaysRef.current.find(o => o._id === id);
    if (!source) return;
    const copy = { ...source, _id: nextIdRef.current++, position: normalizePosition({
      ...source.position, x: source.position.x + 0.02, y: source.position.y + 0.02,
    }, source.type) };
    commitOverlays([...overlaysRef.current, copy]);
    setSelectedId(copy._id);
  }, [commitOverlays]);

  const handleEditorKeyDown = useCallback(e => {
    if (previewClipOpen) return;
    if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
    const mod = e.ctrlKey || e.metaKey;
    const key = e.key.toLowerCase();
    if (mod && (key === 'z' || key === 'y')) {
      e.preventDefault();
      navigateHistory(key === 'y' || e.shiftKey ? 'redo' : 'undo');
    } else if (selectedId != null && mod && key === 'd') {
      e.preventDefault();
      handleDuplicateOverlay(selectedId);
    } else if (selectedId != null && (key === 'delete' || key === 'backspace')) {
      e.preventDefault();
      handleDeleteOverlay(selectedId);
    } else if (key === 'escape') {
      setSelectedId(null);
    } else if (selectedId != null && ['arrowleft', 'arrowright', 'arrowup', 'arrowdown'].includes(key)) {
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      commitOverlays(overlaysRef.current.map(o => o._id !== selectedId ? o : {
        ...o, position: normalizePosition({ ...o.position,
          x: o.position.x + (key === 'arrowleft' ? -step : key === 'arrowright' ? step : 0) / 1920,
          y: o.position.y + (key === 'arrowup' ? -step : key === 'arrowdown' ? step : 0) / 1080,
        }, o.type),
      }), `nudge:${selectedId}`);
    }
  }, [selectedId, navigateHistory, handleDuplicateOverlay, handleDeleteOverlay, commitOverlays, previewClipOpen]);

  const applySyncOffset = useCallback((value, { manual = false } = {}) => {
    const offset = parseSyncOffset(value);
    const next = offset == null ? String(value ?? '') : String(offset);
    syncOffsetRef.current = next;
    setSyncOffset(next);
    syncReadyRef.current = offset != null;
    setSyncReady(offset != null);
    if (manual) {
      setSyncError(null);
      setSyncWarning(null);
    }
    return offset;
  }, [setSyncOffset]);

  const handleDetectSync = useCallback(async () => {
    const requestId = ++syncRequestIdRef.current;
    const previousOffset = parseSyncOffset(syncOffsetRef.current);
    syncReadyRef.current = false;
    setSyncReady(false);
    setSyncDetecting(true);
    setSyncError(null);
    setSyncWarning(null);
    try {
      const result = await detectRenderSync(sessionId);
      if (!mountedRef.current || requestId !== syncRequestIdRef.current) return;
      setSyncInfo(result);
      const decision = classifySyncResult(result);
      if (!decision.usable) {
        applySyncOffset('');
        setSyncError(decision.message);
        setSyncOpen(true);
        return;
      }

      applySyncOffset(decision.offset);
      if (decision.needsReview) {
        setSyncWarning(decision.message);
        setSyncOpen(true);
      }
    } catch (e) {
      if (!mountedRef.current || requestId !== syncRequestIdRef.current) return;
      // A failed re-detection should not discard an already usable manual or
      // detected offset. Initial automatic detection has no prior value and
      // therefore remains blocked until retry/manual correction.
      if (previousOffset != null) applySyncOffset(previousOffset);
      else applySyncOffset('');
      setSyncError(e?.message || 'Automatic sync detection failed. Retry or enter an offset manually.');
      setSyncOpen(true);
    } finally {
      if (mountedRef.current && requestId === syncRequestIdRef.current) setSyncDetecting(false);
    }
  }, [applySyncOffset, sessionId]);

  // A valid offset retained for a render retry can be used immediately. Every
  // fresh upload/editor entry starts empty and runs the exact same detector as
  // the Advanced Sync button before requesting its first preview frame.
  useEffect(() => {
    if (parseSyncOffset(syncOffsetRef.current) != null) {
      syncReadyRef.current = true;
      setSyncReady(true);
      return;
    }
    if (autoDetectStartedRef.current) return;
    autoDetectStartedRef.current = true;
    handleDetectSync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startPreviewPolling = useCallback((jobId) => {
    if (previewPollRef.current) clearTimeout(previewPollRef.current);
    const schedule = (delayMs) => {
      previewPollRef.current = setTimeout(async () => {
        try {
          const status = await getPreviewClipStatus(sessionId, jobId);
          setPreviewStatus(status);
          if (status.status === 'completed') {
            if (status.mp4_url) setPreviewVideoUrl(status.mp4_url);
            return;
          }
          if (status.status === 'failed' || status.status === 'cancelled') return;
        } catch {
          // transient — keep polling
        }
        schedule(2000);
      }, delayMs);
    };
    schedule(3000);
  }, [sessionId]);

  const handleSubmitPreviewClip = useCallback(async () => {
    if (!syncReadyRef.current || syncDetecting) return;
    setPreviewSubmitting(true);
    setPreviewError(null);
    setPreviewJobId(null);
    setPreviewStatus(null);
    setPreviewVideoUrl(null);
    const payload = {
      overlays: serializeOverlays(overlaysRef.current),
      resolution,
      preview_offset_pct: previewOffsetPct,
      preview_duration_sec: previewDurationSec,
    };
    const parsedOffset = parseSyncOffset(syncOffset);
    if (parsedOffset != null) payload.sync_offset_sec = parsedOffset;
    if (videoTransform && videoTransform !== 'none') payload.video_transform = videoTransform;
    try {
      const data = await submitPreviewClip(sessionId, payload);
      setPreviewJobId(data.job_id);
      setPreviewStatus({ status: 'queued', progress_pct: 0 });
      startPreviewPolling(data.job_id);
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewSubmitting(false);
    }
  }, [sessionId, resolution, syncOffset, syncDetecting, videoTransform, previewOffsetPct, previewDurationSec, startPreviewPolling]);

  const handleClosePreviewClip = useCallback(() => {
    if (previewPollRef.current) clearTimeout(previewPollRef.current);
    setPreviewClipOpen(false);
    setPreviewJobId(null);
    setPreviewStatus(null);
    setPreviewVideoUrl(null);
    setPreviewError(null);
  }, []);

  // Clean up poll on unmount
  useEffect(() => () => { if (previewPollRef.current) clearTimeout(previewPollRef.current); }, []);

  const selectedOverlay = overlays.find(o => o._id === selectedId) ?? null;

  // Overlays that need a channel before VTR will accept a render/preview.
  const missingChannel = overlaysMissingChannel(overlays);
  const channelWarning = missingChannel.length > 0
    ? `Assign a channel to ${missingChannel.length} overlay${missingChannel.length > 1 ? 's' : ''} before continuing: ${
        missingChannel.map(o => OVERLAY_TYPES.find(t => t.value === o.type)?.label ?? o.type).join(', ')}`
    : null;
  const syncBlockedMessage = syncDetecting
    ? 'Detecting video sync…'
    : (!syncReady ? 'Detect sync or enter a valid offset before previewing or rendering.' : '');
  const outputBlockedMessage = channelWarning ?? syncBlockedMessage;

  const handleDone = () => {
    if (missingChannel.length > 0) {
      setSelectedId(missingChannel[0]._id);   // surface the first offender for the user to fix
      return;
    }
    if (!syncReady || syncDetecting) {
      setSyncOpen(true);
      return;
    }
    onDone(overlays);
  };

  return (
    <div className={styles.editorRoot} onKeyDown={handleEditorKeyDown}>
      {/* Top bar */}
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={onBack}>← Back</button>
        <span className={styles.editorTitle}>Layout Editor</span>
        <HelpButton panel="videolayouteditor" page="sessions" className={styles.helpBtn} />
        <div className={styles.historyButtons}>
          <button className={styles.refreshBtn} onClick={() => navigateHistory('undo')} disabled={!history.past.length} title="Undo (Ctrl/Cmd+Z)">Undo</button>
          <button className={styles.refreshBtn} onClick={() => navigateHistory('redo')} disabled={!history.future.length} title="Redo (Ctrl/Cmd+Shift+Z)">Redo</button>
        </div>
        <div className={styles.topBarCenter}>
          <label className={styles.transformLabel}>
            Transform
            <select
              className={styles.transformSelect}
              value={videoTransform}
              onChange={e => {
                setVideoTransform?.(e.target.value);
              }}
            >
              <option value="none">None</option>
              <option value="rotate_90_cw">Rotate 90° CW</option>
              <option value="rotate_90_ccw">Rotate 90° CCW</option>
              <option value="rotate_180">Rotate 180°</option>
              <option value="flip_h">Flip Horizontal</option>
              <option value="flip_v">Flip Vertical</option>
            </select>
          </label>
          <button
            className={styles.syncToggle}
            onClick={() => setSyncOpen(v => !v)}
          >
            {syncOpen ? '▾' : '▸'} Advanced Sync Offset
            {syncDetecting ? (
              <span className={styles.syncPending}>Detecting…</span>
            ) : parseSyncOffset(syncOffset) != null && (
              <span className={styles.syncBadge}>{parseSyncOffset(syncOffset).toFixed(3)} s</span>
            )}
          </button>
        </div>
        <div className={styles.topBarRight}>
          <button
            className={styles.previewClipBtn}
            onClick={() => setPreviewClipOpen(true)}
            disabled={missingChannel.length > 0 || !syncReady || syncDetecting}
            title={outputBlockedMessage}
          >
            Preview Clip
          </button>
          {submitError && (
            <span className={styles.submitError}>{submitError}</span>
          )}
          <button
            className={styles.doneBtn}
            onClick={handleDone}
            disabled={submitting || missingChannel.length > 0 || !syncReady || syncDetecting}
            title={outputBlockedMessage}
          >
            {submitting ? 'Submitting…' : 'Render Video'}
          </button>
        </div>
      </div>

      {syncDetecting && <SyncProgress />}

      {/* Validation banner — VTR rejects overlays with no channel assigned */}
      {channelWarning && (
        <div className={styles.validationBanner} onClick={() => setSelectedId(missingChannel[0]._id)}>
          ⚠ {channelWarning}
        </div>
      )}

      {/* Sync offset dropdown */}
      {syncOpen && (
        <div className={styles.syncPanel}>
          <button
            className={styles.syncDetectBtn}
            onClick={() => handleDetectSync()}
            disabled={syncDetecting}
          >
            {syncDetecting ? 'Detecting…' : 'Detect Sync'}
          </button>
          {syncError && <span className={styles.syncError}>{syncError}</span>}
          {syncWarning && <span className={styles.syncWarning}>{syncWarning}</span>}
          {syncInfo && (
            <span className={styles.syncMethod}>Method: {syncInfo.sync_method}</span>
          )}
          {syncInfo?.candidates?.length > 0 && (
            <table className={styles.candidatesTable}>
              <thead>
                <tr>
                  <th>Method</th>
                  <th>Offset (s)</th>
                  <th>Quality</th>
                </tr>
              </thead>
              <tbody>
                {syncInfo.candidates.map(c => (
                  <tr
                    key={c.method}
                    className={c.method === syncInfo.sync_method ? styles.candidateWinner : ''}
                    onClick={() => {
                      if (syncDetecting) return;
                      applySyncOffset(c.offset_sec, { manual: true });
                    }}
                    title="Click to use this offset"
                  >
                    <td>{c.method}</td>
                    <td>{c.offset_sec.toFixed(4)}</td>
                    <td>{c.quality.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <label className={styles.resLabel}>Offset (s)</label>
          <input
            type="number"
            step="0.001"
            className={styles.syncInput}
            placeholder="e.g. 9.909"
            value={syncOffset}
            disabled={syncDetecting}
            onChange={e => applySyncOffset(e.target.value, { manual: true })}
          />
          {isAdmin && setAssertSyncForIndex && (
            <label className={styles.resLabel}
                   style={{ display: 'flex', alignItems: 'center', gap: 6,
                            marginLeft: 12, whiteSpace: 'nowrap' }}
                   title={'Index this render for video search even if automatic sync '
                        + 'confidence is low. Only tick this once you have checked the '
                        + 'preview actually lines up: a wrong offset attaches telemetry '
                        + 'to the wrong frames, and that is invisible once indexed.'}>
              <input
                type="checkbox"
                checked={assertSyncForIndex}
                onChange={e => setAssertSyncForIndex(e.target.checked)}
              />
              Sync verified — index for search
            </label>
          )}
        </div>
      )}

      {/* Three-panel body */}
      <div className={styles.editorBody}>
        <OverlayPalette onAddOverlay={handleAddOverlay} />
        <LayoutCanvas
          overlays={overlays}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onAddOverlay={handleAddOverlay}
          onPositionChange={handlePositionChange}
          frameUrl={preview.background}
          layers={preview.layers}
          frameLoading={preview.loading}
          previewError={preview.error}
          previewStale={preview.stale}
          sessionDuration={sessionDuration}
          previewTimeSec={previewTimeSec}
          onTimeChange={setPreviewTimeSec}
          onRefreshFrame={preview.refresh}
          previewDisabled={!syncReady || syncDetecting}
          previewBlockedMessage={syncBlockedMessage}
        />
        <PropertiesPanel
          key={selectedId}
          overlay={selectedOverlay}
          availableChannels={availableChannels}
          unitPrefs={unitPrefs}
          overlayStyles={overlayStyles}
          onUpdate={handleUpdateOverlay}
          onUpdateMany={handleUpdateOverlayMany}
          onDuplicate={handleDuplicateOverlay}
          onDelete={handleDeleteOverlay}
        />
      </div>

      {/* Preview clip modal */}
      {previewClipOpen && (
        <div className={styles.previewClipOverlay}>
          <div className={styles.previewClipModal}>
            <div className={styles.previewClipHeader}>
              <span className={styles.previewClipTitle}>Preview Clip</span>
              <button className={styles.previewClipClose} onClick={handleClosePreviewClip}>×</button>
            </div>

            {/* Controls — shown when no job is running yet */}
            {!previewJobId && (
              <div className={styles.previewClipControls}>
                <p className={styles.previewClipHint}>
                  Renders a short encoded clip at 720p so you can verify overlay layout and sync before starting a full render.
                </p>
                <div className={styles.previewClipRow}>
                  <label className={styles.previewClipLabel}>Start at</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    className={styles.previewClipSlider}
                    value={previewOffsetPct}
                    onChange={e => setPreviewOffsetPct(parseFloat(e.target.value))}
                  />
                  <span className={styles.previewClipValue}>{Math.round(previewOffsetPct * 100)}% into session</span>
                </div>
                <div className={styles.previewClipRow}>
                  <label className={styles.previewClipLabel}>Duration</label>
                  <input
                    type="number"
                    min="5"
                    max="120"
                    step="1"
                    className={styles.previewClipInput}
                    value={previewDurationSec}
                    onChange={e => setPreviewDurationSec(Math.min(120, Math.max(5, parseInt(e.target.value, 10) || 20)))}
                  />
                  <span className={styles.previewClipValue}>seconds (max 120)</span>
                </div>
                {previewError && <p className={styles.previewClipError}>{previewError}</p>}
                <button
                  className={styles.previewClipGenBtn}
                  onClick={handleSubmitPreviewClip}
                  disabled={previewSubmitting}
                >
                  {previewSubmitting ? 'Submitting…' : 'Generate Preview'}
                </button>
              </div>
            )}

            {/* Progress */}
            {previewJobId && previewStatus?.status !== 'completed' && previewStatus?.status !== 'failed' && (
              <div className={styles.previewClipProgress}>
                <p className={styles.previewClipStatusLabel}>
                  {PREVIEW_STATUS_LABELS[previewStatus?.status] ?? previewStatus?.status ?? 'Starting…'}
                </p>
                {previewStatus?.stage_message && (
                  <p className={styles.previewClipStage}>{previewStatus.stage_message}</p>
                )}
                <div className={styles.previewClipBar}>
                  <div className={styles.previewClipBarFill} style={{ width: `${previewStatus?.progress_pct ?? 0}%` }} />
                </div>
                <p className={styles.previewClipPct}>{previewStatus?.progress_pct ?? 0}%</p>
              </div>
            )}

            {/* Failed */}
            {previewStatus?.status === 'failed' && (
              <div className={styles.previewClipProgress}>
                <p className={styles.previewClipError}>{previewStatus.error || 'Preview render failed.'}</p>
                <button
                  className={styles.previewClipGenBtn}
                  onClick={() => { setPreviewJobId(null); setPreviewStatus(null); setPreviewError(null); }}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Completed — video player */}
            {previewStatus?.status === 'completed' && previewVideoUrl && (
              <div className={styles.previewClipVideoWrap}>
                <video
                  className={styles.previewClipVideo}
                  src={previewVideoUrl}
                  controls
                  autoPlay
                  loop
                />
                <button
                  className={styles.previewClipNewBtn}
                  onClick={() => { setPreviewJobId(null); setPreviewStatus(null); setPreviewVideoUrl(null); setPreviewError(null); }}
                >
                  New Preview
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
